import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Paper,
  Typography,
} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import axios from "../../config/axios";
import { toast } from "react-toastify";

function EditAdmin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    role: "admin",
    status: "active",
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch current user data
        await axios.get("/auth/me");

        // Fetch admin data to edit
        const adminResponse = await axios.get(`/admin/${id}`);
        const adminData = adminResponse.data;

        setFormData({
          ...adminData,
          role: adminData.role || "admin",
          status: adminData.status || "active",
        });
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Error fetching admin data"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.role) {
      newErrors.role = "Role is required";
    }
    if (!formData.status) {
      newErrors.status = "Status is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitLoading(true);
      const response = await axios.put(`/admin/${id}`, formData);
      navigate("/admin", {
        state: {
          message: "Admin updated successfully",
          severity: "success",
        },
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error updating admin");
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress sx={{ color: "#26A69A" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={3}
        sx={{ p: 4, maxWidth: 750, mx: "auto", borderRadius: "12px" }}
      >
        <h1
          style={{
            color: "#263238",
            fontSize: "28px",
            fontWeight: 600,
            marginBottom: "20px",
            marginTop: "-10px",
            textAlign: "center",
          }}
        >
          Edit Admin
        </h1>

        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 3,
              mb: 4,
            }}
          >
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "8px",
                  background: "#F5F5F5",
                },
                "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#26A69A",
                },
              }}
            />

            <FormControl fullWidth error={!!errors.role}>
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                label="Role"
                sx={{
                  borderRadius: "8px",
                  background: "#F5F5F5",
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#26A69A",
                  },
                }}
              >
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
              {errors.role && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: "flex", alignItems: "center" }}
                >
                  <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {errors.role}
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth error={!!errors.status}>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                label="Status"
                sx={{
                  borderRadius: "8px",
                  background: "#F5F5F5",
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#26A69A",
                  },
                }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
              {errors.status && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: "flex", alignItems: "center" }}
                >
                  <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {errors.status}
                </Typography>
              )}
            </FormControl>
          </Box>
          <div
            style={{
              gridColumn: "span 2",
              display: "flex",
              justifyContent: "flex-end",
              gap: "16px",
              marginTop: "24px",
            }}
          >
            <Button
              onClick={() => navigate("/admin")}
              style={{
                background: "#FFFFFF",
                color: "#263238",
                padding: "10px 20px",
                borderRadius: "8px",
                border: "2px solid #263238",
                fontWeight: 500,
                textTransform: "none",
                transition: "all 0.3s",
              }}
              sx={{
                "&:hover": {
                  background: "#263238",
                  color: "#FFFFFF",
                  transform: "scale(1.02)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitLoading}
              style={{
                background: "#FF6F61",
                color: "#FFFFFF",
                padding: "10px 20px",
                borderRadius: "8px",
                fontWeight: 500,
                textTransform: "none",
                transition: "all 0.3s",
              }}
              sx={{
                "&:hover": {
                  background: "#F4511E",
                  transform: "scale(1.02)",
                },
                "&:disabled": {
                  background: "#B0BEC5",
                  cursor: "not-allowed",
                },
              }}
            >
              {submitLoading ? "loading..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </Paper>
    </Box>
  );
}

export default EditAdmin;
